import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { DownloadIcon } from "common/components/Elements/Icon/Icon";
import {
  buildFileName,
  downloadSVGFileAsPNG,
  downloadSVGFileAsPNGByColumns,
} from "common/utilities/browser/downloadFile/downloadFile";
import { viewportWidth } from "common/utilities/browser/viewport/viewport.ts";
import { enqueueInfoNotification } from "core/redux/actions/notification/notificationActions.ts";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import theme from "core/utilities/theme";
import I18n from "i18n/en-US";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import SequenceConservation from "sequenceAnalyser/components/SequenceConservation/SequenceConservation";
import SequenceTableRuler from "sequenceAnalyser/components/Sequences/SequenceTableRuler/SequenceTableRuler";
import { MAX_LOGO_EXPORT_LIMIT } from "sequenceAnalyser/components/Sequences/SequenceTableVisualisations/settings.ts";
import SequenceTableVisualisationWrapper from "sequenceAnalyser/components/Sequences/SequenceTableVisualisationWrapper/SequenceTableVisualisationWrapper";
import { toggleSequenceAnalyserVisualisation } from "sequenceAnalyser/components/TableActions/TableActions";
import { updateVisualisationsSetting } from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import { RESIDUE_PIXEL_WIDTH, RULER_PIXEL_HEIGHT, sequencePixelWidth } from "sequenceAnalyser/utilities/screen/screen";
import { SequenceAnalyserVisualisations } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { longestSequence, sequenceAlignLength } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

const sequenceConservationSVGID = "sequenceConservation";

interface ComponentProps {
  sidebarPixelWidth: number;
  width: number;
  sequencesWidth: number;
  zoomLevel: number;
  conservationHeight: number;
  alignedSequences: SequenceAlign[];
  columnsLetterOccurrences: ColumnsLetterOccurrences;
}

function SequenceConservationVisualization({
  sidebarPixelWidth,
  width,
  sequencesWidth,
  zoomLevel,
  conservationHeight,
  alignedSequences,
  columnsLetterOccurrences,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const settings = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings);
  const scrollPosition = useSelector((state: ApplicationState) => state.sequenceAnalyser.scrollPosition);
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (downloading) {
      const svg = document.querySelector(`#${sequenceConservationSVGID}`) as SVGElement | undefined;
      if (!svg) return;

      const filename = buildFileName("SequenceConservation", "png");
      // downloadSVGFile(svg, filename);
      const onComplete = () => setDownloading(false);

      // Handle selection-based download
      if (selection.positionStart != undefined && selection.positionEnd != undefined) {
        const selectionWidth = selection.positionEnd - selection.positionStart;
        const startPos = selection.positionStart;
        const endPos =
          (selectionWidth > MAX_LOGO_EXPORT_LIMIT ? startPos + MAX_LOGO_EXPORT_LIMIT : selection.positionEnd) + 1;

        if (selectionWidth > MAX_LOGO_EXPORT_LIMIT) {
          dispatch(enqueueInfoNotification(`Logo export limited to ${MAX_LOGO_EXPORT_LIMIT} bp.`));
        }

        downloadSVGFileAsPNGByColumns(svg, filename, startPos, endPos, sequencePixelWidth(), undefined, onComplete);
        return;
      }

      // Handle viewport-based download
      const start = scrollPosition?.x ?? 0;
      const stop = start + viewportWidth() - sidebarPixelWidth;
      downloadSVGFileAsPNG(svg, filename, start, stop, undefined, onComplete);
    }
  }, [dispatch, downloading, scrollPosition?.x, selection.positionEnd, selection.positionStart, sidebarPixelWidth]);

  return (
    <SequenceTableVisualisationWrapper
      title={I18n.SequenceAnalyser.SequenceTableVisualisations.conservation.title}
      sidebarPixelWidth={sidebarPixelWidth}
      width={width}
      height={conservationHeight}
      sidebarActions={
        <>
          <IconButton
            title={I18n.SequenceAnalyser.SequenceTableVisualisations.conservation.download}
            onClick={() => setDownloading(true)}
            sx={{ padding: theme.spacing(0.25, 0.5), "& svg": { fontSize: "14px" } }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            title={I18n.SequenceAnalyser.SequenceTableVisualisations.conservation.close}
            onClick={() => {
              const newViews = toggleSequenceAnalyserVisualisation(
                SequenceAnalyserVisualisations.conservation,
                settings.visualisations,
              );

              dispatch(updateVisualisationsSetting(newViews));
            }}
            sx={{ padding: theme.spacing(0.25, 0.5) }}
          >
            <CloseIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        </>
      }
    >
      <Box sx={{ height: conservationHeight, overflow: "hidden" }}>
        <svg
          id={sequenceConservationSVGID}
          width={sequencesWidth}
          height={conservationHeight + RULER_PIXEL_HEIGHT + 4} // 4px spacing
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <rect x={0} y={0} width="100%" height="100%" fill="#ffffff" />
          <SequenceConservation
            alignedSequences={alignedSequences}
            columnsLetterOccurrences={columnsLetterOccurrences}
            width={sequencesWidth}
            height={conservationHeight}
            columnWidth={RESIDUE_PIXEL_WIDTH}
          />
          {downloading && (
            <SequenceTableRuler
              totalLength={sequenceAlignLength(longestSequence(alignedSequences))}
              zoomLevel={zoomLevel}
              sidebarPixelWidth={sidebarPixelWidth}
              svgOnlyWithYPosition={conservationHeight + 4}
            />
          )}
        </svg>
      </Box>
    </SequenceTableVisualisationWrapper>
  );
}

export default SequenceConservationVisualization;
